import { onboardingQuestionnaire } from '../questionnaires/onboardingQuestionnaire';
import { healthQuestionnaire } from '../questionnaires/healthQuestionnaire';
import { QuestionnaireOption } from '../types';

export function getContraceptionLabel(value: string): string {
  const contraceptionQuestion = onboardingQuestionnaire.sections
    .find((s) => s.id === 'contraception')
    ?.questions.find((q) => q.id === 'q20');

  const option = contraceptionQuestion?.options?.find(
    (opt) => opt.value === value,
  );
  return option?.label || value;
}

export function getMedicationLabel(value: string): string {
  const medicationQuestion = healthQuestionnaire.sections
    .find((s) => s.id === 'health_conditions')
    ?.questions.find((q) => q.id === 'q38');

  const option = medicationQuestion?.options?.find(
    (opt) => opt.value === value,
  );
  return option?.label || value;
}

export function getOptions(
  questionnaireName: 'onboarding' | 'health',
  sectionId: string,
  questionId: string,
): (QuestionnaireOption & { value: string })[] {
  const questionnaire =
    questionnaireName === 'onboarding'
      ? onboardingQuestionnaire
      : healthQuestionnaire;

  const question = questionnaire.sections
    .find((s) => s.id === sectionId)
    ?.questions.find((q) => q.id === questionId);

  return (
    question?.options?.filter(
      (opt): opt is QuestionnaireOption & { value: string } =>
        typeof opt.value === 'string',
    ) || []
  );
}

export function getQuestion(
  questionnaireName: 'onboarding' | 'health',
  sectionId: string,
  questionId: string,
): string {
  const questionnaire =
    questionnaireName === 'onboarding'
      ? onboardingQuestionnaire
      : healthQuestionnaire;

  const question = questionnaire.sections
    .find((s) => s.id === sectionId)
    ?.questions.find((q) => q.id === questionId);

  return question?.question || '';
}

export function getDiagnosisQuestions() {
  return {
    womensHealth: getQuestion('onboarding', 'diagnosis', 'q8'),
    diagnosedBy: getQuestion('onboarding', 'diagnosis', 'q10'),
    diagnosedAge: getQuestion('onboarding', 'diagnosis', 'q11'),
    management: getQuestion('onboarding', 'diagnosis', 'q12'),
    generalHealthConditions: getQuestion('onboarding', 'other_health', 'q25'),
  };
}

export function getDiagnosisOptions() {
  return {
    womensHealthOptions: getOptions('onboarding', 'diagnosis', 'q8'),
    diagnosedByOptions: getOptions('onboarding', 'diagnosis', 'q10'),
    managementOptions: getOptions('onboarding', 'diagnosis', 'q12'),
    generalHealthOptions: getOptions('onboarding', 'other_health', 'q25'),
  };
}

export function getSurgeryQuestionObject() {
  const section = getSection('health', 'pregnancy');
  return section?.questions.find((q) => q.id === 'q37');
}

function getSection(
  questionnaireName: 'onboarding' | 'health',
  sectionId: string,
) {
  const questionnaire =
    questionnaireName === 'onboarding'
      ? onboardingQuestionnaire
      : healthQuestionnaire;

  return questionnaire.sections.find((s) => s.id === sectionId);
}

function getAllQuestionsInSection(
  questionnaireName: 'onboarding' | 'health',
  sectionId: string,
) {
  const section = getSection(questionnaireName, sectionId);
  return section?.questions || [];
}

export function getPregnancySectionQuestions() {
  const pregnancySection = getSection('health', 'pregnancy');
  const pregnancyQuestions =
    pregnancySection?.questions.filter((q) => q.id !== 'q37') || [];

  const onboardingQuestion = getAllQuestionsInSection(
    'onboarding',
    'pregnancy_fertility',
  ).find((q) => q.question === 'Are you currently looking to get pregnant?');

  return {
    pregnancySection,
    pregnancyQuestions,
    onboardingQuestion,
    allQuestions: [
      ...(onboardingQuestion ? [onboardingQuestion] : []),
      ...pregnancyQuestions,
    ],
  };
}

const foodFrequencyQuestion = healthQuestionnaire.sections
  .find((s) => s.id === 'nutrition')
  ?.questions.find((q) => q.id === 'q42-detail');

export const FOOD_FREQUENCY_OPTIONS =
  foodFrequencyQuestion?.type === 'group-detail'
    ? foodFrequencyQuestion.options
    : [];

export function getFoodFrequencyLabel(value: string): string {
  if (!value) return '';
  const option = FOOD_FREQUENCY_OPTIONS?.find((o) => o.value === value);
  return option?.label || value;
}
